import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Square, Zap } from 'lucide-react';
import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  StatusBadge,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import {
  useSubscription,
  useSubscriptionTransition,
  useRunSubscription,
} from '@/hooks/useSubscriptions';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { subscriptionStatusVariants } from '@/config/subscription-status';
import { formatMoney, shortDate } from '@/lib/format';

const cap = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useSubscription(id);
  const nameMap = useCustomerNameMap();
  const transition = useSubscriptionTransition();
  const run = useRunSubscription();

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(s) => {
          const lines = s.templateLines ?? [];
          const busy = transition.isPending || run.isPending;
          return (
            <>
              <PageHeader
                eyebrow={
                  <Link to="/subscriptions" className="inline-flex items-center gap-1 hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5" /> Subscriptions
                  </Link>
                }
                title={`${cap(s.frequency)} subscription`}
                meta={<StatusBadge status={s.status} variants={subscriptionStatusVariants} />}
                actions={
                  <div className="flex gap-2">
                    {!s.platformManaged && s.status === 'DRAFT' && (
                      <Button size="sm" disabled={busy} onClick={() => id && transition.mutate({ id, to: 'ACTIVE' })}>
                        <Play className="h-4 w-4" /> Activate
                      </Button>
                    )}
                    {!s.platformManaged && s.status === 'ACTIVE' && (
                      <>
                        <Button size="sm" disabled={busy} onClick={() => id && run.mutate(id)}>
                          <Zap className="h-4 w-4" /> Run now
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => id && transition.mutate({ id, to: 'PAUSED' })}>
                          <Pause className="h-4 w-4" /> Pause
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => id && transition.mutate({ id, to: 'ENDED' })}>
                          <Square className="h-4 w-4" /> End
                        </Button>
                      </>
                    )}
                    {!s.platformManaged && s.status === 'PAUSED' && (
                      <>
                        <Button size="sm" disabled={busy} onClick={() => id && transition.mutate({ id, to: 'ACTIVE' })}>
                          <Play className="h-4 w-4" /> Resume
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => id && transition.mutate({ id, to: 'ENDED' })}>
                          <Square className="h-4 w-4" /> End
                        </Button>
                      </>
                    )}
                  </div>
                }
              />

              <DashboardGrid columns={2}>
                <DashboardCard title="Schedule">
                  <div className="space-y-1.5 text-[13px]">
                    <Row k="Customer" v={nameMap.get(s.customerId) ?? s.customerId.slice(0, 8)} />
                    {s.planSlug && <Row k="Plan" v={cap(s.planSlug)} />}
                    <Row k="Frequency" v={cap(s.frequency)} />
                    <Row k="Next run" v={shortDate(s.nextRunDate)} />
                    <Row k="Starts" v={s.startDate ? shortDate(s.startDate) : '—'} />
                    <Row k="Ends" v={s.endDate ? shortDate(s.endDate) : '—'} />
                    {s.notes && <Row k="Notes" v={s.notes} />}
                  </div>
                </DashboardCard>
                <DashboardCard title={s.platformManaged ? 'ZAFROI subscription' : 'How it runs'}>
                  <p className="text-[13px] text-muted-foreground">
                    {s.platformManaged ? (
                      'A ZAFROI subscription billed by the platform (the control plane). It shows here for your books — its invoices post to Kosh automatically. Activate / pause / billing are managed in platform admin, not here.'
                    ) : (
                      <>
                        {s.status === 'DRAFT' && 'Activate to start billing on the next run date.'}
                        {s.status === 'ACTIVE' && 'Each run issues an invoice and advances the next run date. "Run now" bills immediately.'}
                        {s.status === 'PAUSED' && 'Paused — no invoices generate until resumed.'}
                        {s.status === 'ENDED' && 'Ended — this schedule no longer bills.'}
                      </>
                    )}
                  </p>
                </DashboardCard>
              </DashboardGrid>

              <DashboardCard title="Invoice template" flushBody>
                {lines.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No template lines.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit price</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{l.description}</TableCell>
                          <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(l.unitPrice, s.currency)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(l.unitPrice * l.quantity, s.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DashboardCard>
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
